import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import LoginPage from "@/pages/LoginPage";
import PasswordResetRequestPage from "@/pages/PasswordResetRequestPage";
import PasswordResetPage from "@/pages/PasswordResetPage";
import SignupPage from "@/pages/SignupPage";
import LandingPage from "@/pages/LandingPage";
import PricingPage from "@/pages/PricingPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import RefundPage from "@/pages/RefundPage";
import OnboardingPage from "@/pages/OnboardingPage";
import AppLayout from "@/layouts/AppLayout";
import PublicLayout from "@/layouts/PublicLayout";
import DashboardPage from "@/pages/DashboardPage";
import OrganizationsPage from "@/pages/OrganizationsPage";
import CustomersPage from "@/pages/CustomersPage";
import InvoicesPage from "@/pages/InvoicesPage";
import InvoiceEditorPage from "@/pages/InvoiceEditorPage";
import InvoiceDetailPage from "@/pages/InvoiceDetailPage";
import TemplatesPage from "@/pages/TemplatesPage";
import InventoryPage from "@/pages/InventoryPage";
import InventoryHistoryPage from "@/pages/InventoryHistoryPage";
import BalanceSheetPage from "@/pages/BalanceSheetPage";
import PaymentPage from "@/pages/PaymentPage";
import SettingsPage from "@/pages/SettingsPage";
import GettingStartedPage from "@/pages/GettingStartedPage";

const qc = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } } });

function ProtectedRoute({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();

  if (!ready) return <div className="p-10 text-slate-500">Loading…</div>;
  if (!user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();

  if (!ready) return <div className="p-10 text-slate-500">Loading…</div>;
  if (user) {
    const redirectPath = new URLSearchParams(location.search).get('redirect') || '/app';
    return <Navigate to={redirectPath} replace />;
  }
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={qc}>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route
                path="/"
                element={
                  <PublicRoute>
                    <PublicLayout>
                      <LandingPage />
                    </PublicLayout>
                  </PublicRoute>
                }
              />
              <Route
                path="/pricing"
                element={
                  <PublicRoute>
                    <PublicLayout>
                      <PricingPage />
                    </PublicLayout>
                  </PublicRoute>
                }
              />
              <Route
                path="/about"
                element={
                  <PublicRoute>
                    <PublicLayout>
                      <AboutPage />
                    </PublicLayout>
                  </PublicRoute>
                }
              />
              <Route
                path="/contact"
                element={
                  <PublicRoute>
                    <PublicLayout>
                      <ContactPage />
                    </PublicLayout>
                  </PublicRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <PublicRoute>
                    <PublicLayout>
                      <SignupPage />
                    </PublicLayout>
                  </PublicRoute>
                }
              />
              <Route
                path="/terms"
                element={
                  <PublicLayout>
                    <TermsPage />
                  </PublicLayout>
                }
              />
              <Route
                path="/privacy"
                element={
                  <PublicLayout>
                    <PrivacyPage />
                  </PublicLayout>
                }
              />
              <Route
                path="/refund"
                element={
                  <PublicLayout>
                    <RefundPage />
                  </PublicLayout>
                }
              />
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <PublicLayout>
                      <LoginPage />
                    </PublicLayout>
                  </PublicRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <PublicLayout>
                      <PasswordResetRequestPage />
                    </PublicLayout>
                  </PublicRoute>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <PublicRoute>
                    <PublicLayout>
                      <PasswordResetPage />
                    </PublicLayout>
                  </PublicRoute>
                }
              />
              <Route
                path="/app"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="onboarding" element={<OnboardingPage />} />
                <Route path="organizations" element={<OrganizationsPage />} />
                <Route path="customers" element={<CustomersPage />} />
                <Route path="inventory" element={<InventoryPage />} />
                <Route path="inventory/:id/history" element={<InventoryHistoryPage />} />
                <Route path="balance-sheet" element={<BalanceSheetPage />} />
                <Route path="payments/new" element={<PaymentPage />} />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="invoices/new" element={<InvoiceEditorPage />} />
                <Route path="invoices/:id" element={<InvoiceDetailPage />} />
                <Route path="invoices/:id/edit" element={<InvoiceEditorPage />} />
                <Route path="templates" element={<TemplatesPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="getting-started" element={<GettingStartedPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
